// Charge math is centralized in @onward-delivery/core (accounting.js), shared with onward-functions
// (NetSuite/Bill.com). Re-exported so the Accounting/Invoice components keep their local import path.
import { calcAccessorialsPerspective, calcSubtotalPerspective, MARKETPLACE_TIERS } from '@onward-delivery/core';

export { getBreakdownPerspective, calcClaims } from '@onward-delivery/core';
export { calcAccessorialsPerspective, calcSubtotalPerspective };

// Core suppresses the carrier payable when shipper and carrier share an organization, so on those
// orders the contractor — not the carrier — holds the cost.
export const isSelfBilled = (order) => {
    const shipperOrg = order?.order_shipper?.user?.organization_id;
    const carrierOrg = order?.order_carrier?.user?.organization_id;

    return !!shipperOrg && shipperOrg === carrierOrg;
};

const ONWARD_BILLED_TIERS = [MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING, MARKETPLACE_TIERS.T3_FULLY_MANAGED];

// The identifier shown in the "Order" column of the completed-orders and invoice tables. Onward-billed
// tiers omit the customer reference; every tier leads with the order number so a row stays findable by it.
export const formatOrderIdentifier = (order) => {
    const isOnward = ONWARD_BILLED_TIERS.includes(order?.shipper_rate_override?.marketplace_tier);
    const segments = isOnward
        ? [order?.order_number, order?.po_number]
        : [order?.order_number, order?.po_number, order?.reference_id];

    return segments.filter(Boolean).join(' / ');
};

const PAYABLE_PERSPECTIVES = {
    carrier: { idField: 'carrier_id', rateField: 'carrier_rate', adjustmentKey: 'carrier' },
    mm_carrier: {
        idField: 'middle_mile_carrier_id',
        rateField: 'middle_mile_carrier_rate',
        adjustmentKey: 'mmCarrier',
    },
    wh_carrier: { idField: 'warehouse_carrier_id', rateField: 'warehouse_carrier_rate', adjustmentKey: 'whCarrier' },
    contractor: { idField: 'contractor_id', rateField: 'contractor_rate', adjustmentKey: 'contractor' },
};

const RECEIVABLE_PERSPECTIVES = ['shipper', 'mm_shipper', 'wh_shipper'];

const isReceivable = (type) => ['RECEIVABLE', 'ONWARD_RECEIVABLE'].includes(type);
const isPayable = (type) => ['PAYABLE', 'ONWARD_PAYABLE'].includes(type);

// Mirrors core's payableLegOrders. The perspective travels beside the order rather than on it: one client
// can hold two legs of the same order, so the bucket a row came from is the only thing that separates them.
export const invoiceLegRows = (invoice) => {
    if (isReceivable(invoice?.type)) {
        return (invoice?.shipper_orders || []).map((order) => ({ order, perspective: null }));
    }
    if (isPayable(invoice?.type)) {
        return [
            ...(invoice?.orders || []).map((order) => ({ order, perspective: 'carrier' })),
            ...(invoice?.middle_mile_carrier_orders || []).map((order) => ({ order, perspective: 'mm_carrier' })),
            ...(invoice?.warehouse_carrier_orders || []).map((order) => ({ order, perspective: 'wh_carrier' })),
            ...(invoice?.contractor_orders || []).map((order) => ({ order, perspective: 'contractor' })),
        ];
    }
    if (invoice?.type === 'INTERNAL') {
        return (invoice?.orders || []).map((order) => ({ order, perspective: 'internal' }));
    }

    return [];
};

export const legRowId = ({ order, perspective } = {}) =>
    perspective ? `${order?.order_id}_${perspective}` : order?.order_id;

// The per-order event an invoice's approval fans out, keyed by the bucket a row came from. Mirrors
// onward-functions src/triggers/carrier_invoices/onUpdate.f.js, which fires carrier_invoice_approved for
// every order on `orders` — the carrier leg of a payable and every row of an INTERNAL invoice.
const APPROVED_EVENTS = {
    carrier: 'carrier_invoice_approved',
    internal: 'carrier_invoice_approved',
    mm_carrier: 'middle_mile_carrier_invoice_approved',
    wh_carrier: 'warehouse_carrier_invoice_approved',
    contractor: 'contractor_invoice_approved',
};

export const invoiceApprovedEvent = (invoice, perspective) =>
    isReceivable(invoice?.type) ? 'shipper_invoice_approved' : APPROVED_EVENTS[perspective] || null;

// Callers holding a row pass its perspective. For those that only have ids, a client on two legs of one
// order is ambiguous and resolves to nothing rather than to whichever leg the key order happens to hit.
const payablePerspective = (invoice, order, perspective) => {
    if (perspective) return PAYABLE_PERSPECTIVES[perspective] ? perspective : null;

    const matches = Object.keys(PAYABLE_PERSPECTIVES).filter(
        (p) => invoice?.client_id === order?.[PAYABLE_PERSPECTIVES[p].idField]
    );

    return matches.length === 1 ? matches[0] : null;
};

const payableFields = (invoice, order, perspective) =>
    PAYABLE_PERSPECTIVES[payablePerspective(invoice, order, perspective)];

const perByType = (perspectiveFn) => (invoice, order, perspective) => {
    if (isReceivable(invoice?.type)) {
        return RECEIVABLE_PERSPECTIVES.reduce((sum, p) => sum + (perspectiveFn(order, p) || 0), 0);
    }
    if (isPayable(invoice?.type)) {
        const billed = payablePerspective(invoice, order, perspective);
        return billed ? perspectiveFn(order, billed) || 0 : 0;
    }
    if (invoice?.type === 'INTERNAL') return perspectiveFn(order, 'internal') || 0;

    return 0;
};

export const calcSubtotal = perByType(calcSubtotalPerspective);
export const calcAccessorials = perByType(calcAccessorialsPerspective);

export const hasShipperMiddleMile = (invoice) => {
    if (!['RECEIVABLE', 'ONWARD_RECEIVABLE'].includes(invoice.type)) return false;
    return (invoice.shipper_orders || []).some((order) => !!order.middle_mile_shipper_rate_override_id);
};

export const hasShipperWarehouse = (invoice) => {
    if (!['RECEIVABLE', 'ONWARD_RECEIVABLE'].includes(invoice.type)) return false;
    return (invoice.shipper_orders || []).some((order) => !!order.warehouse_shipper_rate_override_id);
};

export const calcIsManual = (invoice, order, perspective) => {
    const adjustments = order?.order_revenue_adjustments;

    if (isReceivable(invoice?.type)) {
        return !!(adjustments?.shipper || adjustments?.mmShipper || adjustments?.whShipper);
    }
    if (isPayable(invoice?.type)) {
        const fields = payableFields(invoice, order, perspective);
        return !!(fields && adjustments?.[fields.adjustmentKey]);
    }
    if (invoice?.type === 'INTERNAL') return !!adjustments?.internal;

    return false;
};

export const calcTotal = (invoice, order, perspective) => {
    if (isReceivable(invoice?.type)) {
        return (
            (order?.shipper_rate || 0) + (order?.middle_mile_shipper_rate || 0) + (order?.warehouse_shipper_rate || 0)
        );
    }
    if (isPayable(invoice?.type)) {
        const fields = payableFields(invoice, order, perspective);
        return (fields && order?.[fields.rateField]) || 0;
    }
    if (invoice?.type === 'INTERNAL') return order?.order_revenue || 0;

    return 0;
};
